// ===== FILE: src/pages/DashboardProject/ContractorLocationList.tsx ===== (UPDATED)
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Input } from 'reactstrap';
import { Link } from 'react-router-dom';
import AutoBreadCrumb from '../../Components/Common/AutoBreadCrumb';
import { contractorAPI, LocationWithCount } from '../../services/contractorService';
import { buildStateUrl, buildCityUrl, getLocationSlug } from '../../utils/urlUtils';
import Header from "../Home/Header";
import Footer from "../Home/Footer";
import SEOTags from './SEOTags';

const ContractorLocationList: React.FC = () => {
    const [locations, setLocations] = useState<LocationWithCount[]>([]);
    const [filteredLocations, setFilteredLocations] = useState<LocationWithCount[]>([]);
    const [selectedState, setSelectedState] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchLocations = async () => {
            setLoading(true);
            setError(null);

            try {
                const response = await contractorAPI.getLocationsWithCounts();
                
                // Process locations to separate states and cities
                const stateMap = new Map<string, number>();
                const cityMap = new Map<string, { state: string; count: number }>();

                response.data.forEach(location => {
                    // Add state count
                    const currentStateCount = stateMap.get(location.state) || 0;
                    stateMap.set(location.state, currentStateCount + location.count);

                    // Add city if it exists
                    if (location.city) {
                        cityMap.set(`${location.city}, ${location.state}`, {
                            state: location.state,
                            count: location.count
                        });
                    }
                });

                // Convert to arrays
                const stateLocations = Array.from(stateMap.entries()).map(([state, count]) => ({
                    state,
                    count
                }));

                const cityLocations = Array.from(cityMap.entries()).map(([cityState, data]) => {
                    const [city, state] = cityState.split(', ');
                    return {
                        state,
                        city,
                        count: data.count
                    };
                });

                // Combine and sort
                const allLocations = [
                    ...stateLocations,
                    ...cityLocations
                ].sort((a, b) => b.count - a.count);

                setLocations(allLocations);
                setFilteredLocations(allLocations);
            } catch (err) {
                console.error('Error fetching locations:', err);
                setError('Failed to load locations. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchLocations();
    }, []);

    // Filter locations based on search and state selection
    useEffect(() => {
        let filtered = locations;

        if (selectedState) {
            filtered = filtered.filter(location => location.state === selectedState);
        }

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(location =>
                location.state.toLowerCase().includes(term) ||
                (location.city && location.city.toLowerCase().includes(term))
            );
        }

        setFilteredLocations(filtered);
    }, [locations, selectedState, searchTerm]);

    const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedState(e.target.value);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const uniqueStates = Array.from(new Set(locations.map(loc => loc.state))).sort();

    return (
        <React.Fragment>
            <Header isFullWidth={true} />
            <div className="page-content">
                <SEOTags
                    title="Contractor Locations Directory"
                    description="Browse contractors by location. Find local contractors in your state and city."
                    schemaType="ItemList"
                />

                <Container fluid>
                    <AutoBreadCrumb />

                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader>
                                    <Row className="align-items-center">
                                        <Col>
                                            <h4 className="card-title mb-0">Contractor Locations</h4>
                                        </Col>
                                        <Col md={6}>
                                            <Row>
                                                <Col md={6}>
                                                    <select
                                                        className="form-select"
                                                        value={selectedState}
                                                        onChange={handleStateChange}
                                                    >
                                                        <option value="">All States</option>
                                                        {uniqueStates.map(state => (
                                                            <option key={state} value={state}>
                                                                {state}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </Col>
                                                <Col md={6}>
                                                    <Input
                                                        type="text"
                                                        placeholder="Search locations..."
                                                        value={searchTerm}
                                                        onChange={handleSearchChange}
                                                    />
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                </CardHeader>

                                <CardBody>
                                    {loading ? (
                                        <div className="d-flex justify-content-center p-4">
                                            <div className="spinner-border" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </div>
                                    ) : error ? (
                                        <div className="alert alert-danger">{error}</div>
                                    ) : filteredLocations.length > 0 ? (
                                        <Row>
                                            {filteredLocations.map((location, index) => (
                                                <Col md={6} lg={4} xl={3} className="mb-3" key={index}>
                                                    <div className="card border h-100">
                                                        <div className="card-body">
                                                            {location.city ? (
                                                                <Link
                                                                    to={buildCityUrl(location.state, location.city)}
                                                                    className="text-decoration-none"
                                                                >
                                                                    <h6 className="card-title text-primary mb-1">
                                                                        {location.city}, {location.state}
                                                                    </h6>
                                                                </Link>
                                                            ) : (
                                                                <Link
                                                                    to={buildStateUrl(location.state)}
                                                                    className="text-decoration-none"
                                                                >
                                                                    <h6 className="card-title text-primary mb-1">
                                                                        {location.state}
                                                                    </h6>
                                                                </Link>
                                                            )}
                                                            <p className="card-text text-muted mb-0">
                                                                {location.count} contractor{location.count !== 1 ? 's' : ''}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>
                                    ) : (
                                        <div className="text-center py-4">
                                            <p className="text-muted">No locations found matching your criteria.</p>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
            <Footer />
        </React.Fragment>
    );
};

export default ContractorLocationList;